import React from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import AdminDateInput from "../AdminDateInput";
import { formatInr, formatInrWithWords } from "../../utils/financeFormat";
import TableTotalAmount, { ANALYTICS_TABLE_TOTAL_ROW_CLASS } from "./TableTotalAmount";
import MonthlyBankCashHero from "./MonthlyBankCashHero";
import MonthlyCashPaymentPanel from "./MonthlyCashPaymentPanel";
import { MonthlyMetricTrend } from "./MonthlyCostTabShell";

function formatIsoDateIn(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(new Date(`${iso}T12:00:00`));
  } catch {
    return iso;
  }
}

export function VendorOpeningBalancePanel({
  settings,
  onChange,
  onSave,
  saving,
  bankKeywordHint,
}) {
  return (
    <div className="border border-secondary rounded p-3 mb-3">
      <h5 className="h6 text-white mb-2">Opening balance &amp; cutoff date</h5>
      <p className="text-secondary small mb-2">
        Configure once for production — used for net payable calculation. Bank narration keyword:{" "}
        <code className="text-info">{bankKeywordHint}</code>. Increase opening balance when you receive
        goods on credit before paying via bank.
      </p>
      <div className="row g-2 align-items-end">
        <div className="col-md-4">
          <Form.Label className="text-secondary small">Opening balance ₹ (debt owed)</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            min={0}
            className="text-bg-dark border-secondary"
            value={settings?.openingBalanceRupee ?? 0}
            onChange={(e) =>
              onChange({ ...settings, openingBalanceRupee: Number(e.target.value) || 0 })
            }
          />
        </div>
        <div className="col-md-4">
          <Form.Label className="text-secondary small">Cutoff date (as-of)</Form.Label>
          <AdminDateInput
            size="sm"
            value={settings?.openingBalanceDateIso || ""}
            onChange={(e) =>
              onChange({ ...settings, openingBalanceDateIso: e.target.value })
            }
          />
        </div>
        <div className="col-md-4">
          <Button size="sm" variant="success" disabled={saving} onClick={onSave}>
            {saving ? "Saving…" : "Save opening settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function VendorPayableMonthlyTab({
  vendorLabel,
  bankKeywordHint,
  periodMonth,
  comparison,
  trendLabel,
  valueFn,
  totalRupee,
  debitRows,
  ledger,
  ledgerBusy,
  settings,
  onSettingsChange,
  onSaveSettings,
  settingsBusy,
  onRefreshLedger,
  lineHint,
  emptyMessage,
  cashSlotId,
  cashAmountRupee,
  cashLines = [],
  cashSaving,
  onAddCashLine,
  onDeleteCashLine,
  cashHint,
}) {
  const bankTotal = Number(totalRupee) || 0;
  const cashTotal = Number(cashAmountRupee) || 0;
  const monthTotal = bankTotal + cashTotal;
  const lines = debitRows || [];

  return (
    <>
      <MonthlyMetricTrend metricLabel={trendLabel} valueFn={valueFn} comparison={comparison} />
      <MonthlyBankCashHero
        label={vendorLabel}
        periodMonth={periodMonth}
        bankRupee={bankTotal}
        cashRupee={cashTotal}
        extraMeta={
          ledger ? (
            <>
              <div>
                Net debt: <strong className="text-info">{formatInr(ledger.netPayableRupee)}</strong>
              </div>
              <div>
                Bank lines: <strong className="text-light">{lines.length}</strong>
              </div>
            </>
          ) : null
        }
      />

      <VendorOpeningBalancePanel
        settings={settings}
        onChange={onSettingsChange}
        onSave={onSaveSettings}
        saving={settingsBusy}
        bankKeywordHint={bankKeywordHint}
      />

      {ledgerBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
      {ledger ? (
        <>
          <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
            <thead>
              <tr>
                <th>Metric</th>
                <th className="text-end">₹</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>This month paid ({periodMonth})</td>
                <td className="text-end text-warning fs-6">{formatInrWithWords(ledger.monthSpendRupee)}</td>
              </tr>
              <tr>
                <td>Opening balance (as of {formatIsoDateIn(ledger.openingBalanceDateIso)})</td>
                <td className="text-end">{formatInr(ledger.openingBalanceRupee)}</td>
              </tr>
              <tr>
                <td>− Paid via bank after cutoff</td>
                <td className="text-end text-success">{formatInr(ledger.bankPaymentsAfterOpeningRupee)}</td>
              </tr>
              <tr className="fw-bold">
                <td>Net payable to vendor (debt)</td>
                <td className="text-end text-info fs-6">{formatInrWithWords(ledger.netPayableRupee)}</td>
              </tr>
            </tbody>
          </Table>
          <Button size="sm" variant="outline-light" className="mb-3" onClick={onRefreshLedger} disabled={ledgerBusy}>
            Refresh debt ledger
          </Button>
        </>
      ) : null}

      {lineHint ? <p className="text-secondary small mb-2">{lineHint}</p> : null}
      {cashSlotId ? (
        <MonthlyCashPaymentPanel
          slotId={cashSlotId}
          label={`${vendorLabel} — cash supplement`}
          hint={cashHint || "Optional cash paid on top of bank — included in monthly cash total."}
          lines={cashLines}
          totalRupee={cashAmountRupee}
          saving={cashSaving}
          onAddLine={onAddCashLine}
          onDeleteLine={onDeleteCashLine}
        />
      ) : null}

      <div className="finance-recon-scroll">
        <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Bank</th>
              <th>Category</th>
              <th>Particulars</th>
              <th className="text-end">Amount ₹</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-secondary small">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              <>
                {lines.map((r, idx) => (
                  <tr key={`vp-${r.txnDateIso}-${idx}`}>
                    <td>{r.txnDateIso}</td>
                    <td>{r.bank}</td>
                    <td>{r.category || "—"}</td>
                    <td className="small">{r.particulars}</td>
                    <td className="text-end text-warning fw-semibold">{formatInr(r.debitRupee)}</td>
                  </tr>
                ))}
                <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                  <td colSpan={4}>
                    Total ({lines.length} {lines.length === 1 ? "line" : "lines"})
                  </td>
                  <td className="text-end fs-6">
                    <TableTotalAmount amount={monthTotal} toneClass="text-warning" />
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
}
